<script setup lang="ts">
const props = defineProps<{
  badge: TMissionOrBadge;
}>();

const emit = defineEmits<{
  (e: 'open-detail', badge: TMissionOrBadge): void;
}>();

const slotRef = ref<HTMLElement | null>(null);

const isCompleted = computed(() => props.badge.is_completed === true);

// Nome do país (badge.name vem como "BRASIL - Name Real", mantemos só a 1ª parte)
const displayName = computed(() => {
  const raw = props.badge.name ?? '';

  return raw.split(' - ')[0]?.trim() || raw;
});

function handleClick() {
  emit('open-detail', props.badge);
}
</script>

<template>
  <div
    ref="slotRef"
    class="sticker-slot"
    :class="{ 'is-collected': isCompleted }"
    @click="handleClick"
  >
    <span class="sticker-number">{{ badge.position ?? '' }}</span>
    <div class="sticker-img-wrapper">
      <img
        :alt="displayName"
        class="sticker-img"
        :class="isCompleted ? 'is-unlocked' : 'is-locked'"
        decoding="async"
        draggable="false"
        loading="lazy"
        :src="badge.image"
        @dragstart.prevent
      />
    </div>
  </div>
</template>

<style scoped>
.sticker-slot {
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 4;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
}

.sticker-slot:hover {
  transform: scale(1.02) translateY(-2px);
}

.sticker-number {
  position: absolute;
  top: 4px;
  right: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 18px;
  font-weight: 300;
  color: #fff;
  z-index: 10;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.9);
  pointer-events: none;
  line-height: 1;
}

.sticker-img-wrapper {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sticker-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.sticker-img.is-unlocked {
  opacity: 1;
  animation: sticker-aura-pulse 2.4s ease-in-out infinite;
}

@keyframes sticker-aura-pulse {
  0%,
  100% {
    filter: drop-shadow(0 0 2px rgba(190, 210, 255, 0.85))
      drop-shadow(0 0 6px rgba(80, 120, 255, 0.7))
      drop-shadow(0 0 12px rgba(41, 41, 255, 0.45))
      drop-shadow(0 6px 10px rgba(0, 0, 0, 0.4));
  }
  50% {
    filter: drop-shadow(0 0 3px rgba(210, 225, 255, 1))
      drop-shadow(0 0 9px rgba(80, 120, 255, 0.9))
      drop-shadow(0 0 18px rgba(41, 41, 255, 0.6))
      drop-shadow(0 6px 10px rgba(0, 0, 0, 0.4));
  }
}

.sticker-img.is-locked {
  opacity: 0.75;
  filter: grayscale(95%) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4));
}

.sticker-slot.is-collected:hover .sticker-img {
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.5))
    drop-shadow(0 0 8px rgba(41, 41, 255, 0.25));
}

@media (max-width: 767px) {
  .sticker-img {
    width: 106.213px;
    height: 144.329px;
  }
}
</style>
